import type { ListOfferPurchaseRequests200 } from "@packages/api-client/types/marketplaceController";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Check,
	ChevronDown,
	Clock,
	Inbox,
	MailQuestion,
	MessageSquareQuote,
	PackageOpen,
	ShieldCheck,
	Store,
	X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	useListMyMarketplaceOffers,
	useListOfferPurchaseRequests,
} from "@/api/marketplace/offers";
import {
	useAcceptPurchaseRequest,
	useRejectPurchaseRequest,
} from "@/api/marketplace/requests";
import { Button } from "@/components/ui/button";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { cn } from "@/utils/utils";
import { seeded } from "../-components/CowPrintArt";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/marketplace/incoming-requests/",
)({
	component: IncomingRequestsPage,
});

type PurchaseRequest = ListOfferPurchaseRequests200[number];

/* -------------------------------------------------------------------------- */
/*  Status presentation                                                       */
/* -------------------------------------------------------------------------- */

const STATUS_ORDER = [
	"PENDING",
	"ACCEPTED",
	"REJECTED",
	"CANCELLED",
	"EXPIRED",
] as const;

const STATUS_STYLES: Record<string, { badge: string; dot: string }> = {
	PENDING: { badge: "bg-warning/15 text-warning", dot: "bg-warning" },
	ACCEPTED: { badge: "bg-success/15 text-success", dot: "bg-success" },
	REJECTED: { badge: "bg-error/10 text-error", dot: "bg-error" },
	CANCELLED: {
		badge: "bg-base-200 text-base-content/50",
		dot: "bg-base-content/40",
	},
	EXPIRED: {
		badge: "bg-base-200 text-base-content/50",
		dot: "bg-base-content/40",
	},
};

function RequestStatusBadge({ status }: { status: string }) {
	const { t } = useTranslation();
	const s = STATUS_STYLES[status] ?? STATUS_STYLES.PENDING;
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
				s.badge,
			)}
		>
			<span className={cn("size-1.5 rounded-full", s.dot)} />
			{t(`marketplace.requestStatus.${status}`, status)}
		</span>
	);
}

function daysUntil(iso?: string): number | null {
	if (!iso) return null;
	return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

/* -------------------------------------------------------------------------- */
/*  Buyer monogram — deterministic colored avatar keyed off the org id        */
/* -------------------------------------------------------------------------- */

const AVATAR_GRADIENTS = [
	"from-emerald-400 to-teal-500",
	"from-amber-400 to-orange-500",
	"from-sky-400 to-indigo-500",
	"from-lime-400 to-green-500",
	"from-fuchsia-400 to-purple-500",
	"from-rose-400 to-pink-500",
];

function initialsOf(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function BuyerMonogram({
	name,
	seed,
	className,
}: {
	name: string;
	seed: string;
	className?: string;
}) {
	const gradient =
		AVATAR_GRADIENTS[Math.floor(seeded(seed, 13) * AVATAR_GRADIENTS.length)];
	return (
		<div
			className={cn(
				"grid shrink-0 place-items-center rounded-xl bg-gradient-to-br font-bold text-white shadow-sm",
				gradient,
				className,
			)}
			aria-hidden
		>
			{initialsOf(name)}
		</div>
	);
}

/* -------------------------------------------------------------------------- */
/*  Request card                                                              */
/* -------------------------------------------------------------------------- */

function RequestCard({
	req,
	index,
	headCount,
	confirming,
	typedName,
	onStartAccept,
	onCancelAccept,
	onTypedNameChange,
	onConfirmAccept,
	onReject,
	accepting,
	rejecting,
}: {
	req: PurchaseRequest;
	index: number;
	headCount: number;
	confirming: boolean;
	typedName: string;
	onStartAccept: () => void;
	onCancelAccept: () => void;
	onTypedNameChange: (v: string) => void;
	onConfirmAccept: () => void;
	onReject: () => void;
	accepting: boolean;
	rejecting: boolean;
}) {
	const { t } = useTranslation();
	const { formatDateWithOptions } = useDateFormatter();
	const isPending = req.status === "PENDING";
	const nameMatches = typedName.trim() === req.buyerOrganizationName.trim();
	const expiresInDays = isPending ? daysUntil(req.expiresAt) : null;
	const requestedOn = req.createdAt
		? formatDateWithOptions(new Date(req.createdAt).getTime(), {
				day: "numeric",
				month: "short",
				year: "numeric",
			})
		: null;

	return (
		<div
			className={cn(
				"flex flex-col gap-4 rounded-xl border border-base-300 bg-base-100 p-3 shadow-sm transition-all duration-200 animate-in fade-in slide-in-from-bottom-2 sm:p-4",
				isPending && "hover:border-primary/30 hover:shadow-md",
			)}
			style={{ animationDelay: `${Math.min(index * 60, 360)}ms` }}
		>
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start">
				<BuyerMonogram
					name={req.buyerOrganizationName}
					seed={req.buyerOrganizationId}
					className="size-12 text-base sm:size-14 sm:text-lg"
				/>

				<div className="flex min-w-0 flex-1 flex-col gap-1.5">
					<div className="flex flex-wrap items-center gap-2">
						<RequestStatusBadge status={req.status} />
						{requestedOn && (
							<span className="text-xs text-base-content/50">
								{t("marketplace.incoming.requested", { date: requestedOn })}
							</span>
						)}
						{expiresInDays != null && expiresInDays >= 0 && (
							<span className="inline-flex items-center gap-1 text-xs text-base-content/50">
								<Clock className="size-3" />
								{t("marketplace.incoming.expiresIn", { n: expiresInDays })}
							</span>
						)}
					</div>
					<h3 className="truncate text-base font-semibold leading-snug text-base-content">
						{req.buyerOrganizationName}
					</h3>
					{req.message ? (
						<p className="flex items-start gap-1.5 text-sm text-base-content/70">
							<MessageSquareQuote className="mt-0.5 size-3.5 shrink-0 text-base-content/40" />
							<span className="italic">{req.message}</span>
						</p>
					) : null}
				</div>

				{isPending && !confirming && (
					<div className="relative z-10 flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
						<Button size="sm" onClick={onStartAccept} className="rounded-full">
							<Check className="size-3.5" />
							{t("marketplace.incoming.accept")}
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={onReject}
							disabled={rejecting}
							className="rounded-full"
						>
							<X className="size-3.5" />
							{t("marketplace.incoming.reject")}
						</Button>
					</div>
				)}
			</div>

			{confirming && (
				<div className="space-y-3 rounded-lg border border-warning/30 bg-warning/5 p-3 sm:p-4">
					<div className="flex items-start gap-2">
						<ShieldCheck className="mt-0.5 size-5 shrink-0 text-warning" />
						<p className="text-sm text-base-content/80">
							{t("marketplace.incoming.confirmIntro", {
								n: headCount,
								buyer: req.buyerOrganizationName,
							})}{" "}
							<span className="font-medium">
								{t("marketplace.incoming.confirmHint")}
							</span>
						</p>
					</div>
					<input
						className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
						value={typedName}
						placeholder={req.buyerOrganizationName}
						onChange={(e) => onTypedNameChange(e.target.value)}
						// biome-ignore lint/a11y/noAutofocus: focus the confirmation field when the panel opens
						autoFocus
					/>
					<div className="flex justify-end gap-2">
						<Button
							size="sm"
							variant="ghost"
							onClick={onCancelAccept}
							className="rounded-full"
						>
							{t("common.cancel")}
						</Button>
						<Button
							size="sm"
							disabled={!nameMatches || accepting}
							onClick={onConfirmAccept}
							className="rounded-full"
						>
							<Check className="size-3.5" />
							{t("marketplace.incoming.confirmTransfer")}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

function IncomingRequestsPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const { data: offers, isLoading: offersLoading } =
		useListMyMarketplaceOffers(orgId);
	const [offerId, setOfferId] = useState("");
	const selectedOffer = offers?.find((o) => o.id === offerId);

	const { data: requests, isLoading } = useListOfferPurchaseRequests(
		orgId,
		offerId,
		{ query: { enabled: !!offerId } },
	);

	const accept = useAcceptPurchaseRequest();
	const reject = useRejectPurchaseRequest();
	const [confirmingId, setConfirmingId] = useState<string | null>(null);
	const [typedName, setTypedName] = useState("");
	const [filter, setFilter] = useState<string>("ALL");

	const list = useMemo(() => requests ?? [], [requests]);
	const counts = useMemo(() => {
		const c: Record<string, number> = {};
		for (const r of list) c[r.status] = (c[r.status] ?? 0) + 1;
		return c;
	}, [list]);
	const visible = useMemo(
		() => (filter === "ALL" ? list : list.filter((r) => r.status === filter)),
		[list, filter],
	);
	const activeStatuses = STATUS_ORDER.filter((s) => (counts[s] ?? 0) > 0);
	const pendingCount = counts.PENDING ?? 0;
	const hasOffers = (offers?.length ?? 0) > 0;

	return (
		<div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
			{/* Hero header */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex items-center gap-3">
					<div className="relative grid size-11 place-items-center rounded-xl bg-primary text-primary-content shadow-sm">
						<Inbox className="size-6" />
						{pendingCount > 0 && (
							<span className="absolute -right-1.5 -top-1.5 grid size-5 place-items-center rounded-full bg-warning text-[10px] font-bold text-warning-content shadow">
								{pendingCount}
							</span>
						)}
					</div>
					<div>
						<h1 className="text-2xl font-bold tracking-tight">
							{t("marketplace.incoming.title")}
						</h1>
						<p className="text-sm text-base-content/60">
							{t("marketplace.incoming.subtitle")}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Button asChild variant="outline" className="rounded-full">
						<Link to="/app/org/$orgId/marketplace/my-offers" params={{ orgId }}>
							<Store className="size-4" />
							{t("marketplace.myOffers.title")}
						</Link>
					</Button>
				</div>
			</div>

			{/* Offer selector control bar */}
			{hasOffers && (
				<div className="mt-6 flex flex-col gap-3 rounded-xl border border-base-300 bg-base-100 p-3 shadow-sm sm:flex-row sm:items-center sm:gap-4">
					<label className="min-w-0 flex-1 space-y-1.5">
						<span className="text-xs font-medium uppercase tracking-wide text-base-content/50">
							{t("marketplace.incoming.offer")}
						</span>
						<div className="relative">
							<Store className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-base-content/40" />
							<select
								className="w-full appearance-none rounded-lg border border-base-300 bg-base-100 py-2.5 pl-9 pr-9 text-sm font-medium outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
								value={offerId}
								onChange={(e) => {
									setOfferId(e.target.value);
									setConfirmingId(null);
									setFilter("ALL");
								}}
							>
								<option value="">
									{t("marketplace.incoming.selectOffer")}
								</option>
								{offers?.map((o) => (
									<option key={o.id} value={o.id}>
										{o.title} ({t(`marketplace.offerStatus.${o.status}`)},{" "}
										{t("marketplace.headCount", { n: o.headCount })})
									</option>
								))}
							</select>
							<ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-base-content/40" />
						</div>
					</label>
					{selectedOffer && (
						<div className="flex items-center gap-2 sm:flex-col sm:items-end sm:gap-1">
							<span className="text-sm font-semibold text-base-content">
								{t("marketplace.headCount", { n: selectedOffer.headCount })}
							</span>
							{pendingCount > 0 ? (
								<span className="inline-flex items-center gap-1.5 rounded-full bg-warning/15 px-2.5 py-1 text-xs font-semibold text-warning">
									<span className="size-1.5 rounded-full bg-warning" />
									{t("marketplace.incoming.pending", { n: pendingCount })}
								</span>
							) : (
								<span className="text-xs text-base-content/50">
									{t(`marketplace.offerStatus.${selectedOffer.status}`)}
								</span>
							)}
						</div>
					)}
				</div>
			)}

			{/* No offers at all */}
			{!offersLoading && !hasOffers && (
				<EmptyState
					icon={<PackageOpen className="size-8" />}
					title={t("marketplace.incoming.noOffersTitle")}
					hint={t("marketplace.incoming.noOffersHint")}
					cta={
						<Button asChild className="mt-6 rounded-full">
							<Link
								to="/app/org/$orgId/marketplace/my-offers"
								params={{ orgId }}
							>
								<Store className="size-4" />
								{t("marketplace.incoming.noOffersCta")}
							</Link>
						</Button>
					}
				/>
			)}

			{/* Nudge to pick an offer */}
			{hasOffers && !offerId && (
				<EmptyState
					icon={<MailQuestion className="size-8" />}
					title={t("marketplace.incoming.pickTitle")}
					hint={t("marketplace.incoming.pickHint")}
				/>
			)}

			{/* Status filter pills */}
			{offerId && !isLoading && list.length > 0 && (
				<div className="mt-6 flex flex-wrap gap-2">
					<FilterPill
						active={filter === "ALL"}
						onClick={() => setFilter("ALL")}
						label={t("marketplace.incoming.all")}
						count={list.length}
					/>
					{activeStatuses.map((s) => (
						<FilterPill
							key={s}
							active={filter === s}
							onClick={() => setFilter(s)}
							label={t(`marketplace.requestStatus.${s}`, s)}
							count={counts[s] ?? 0}
							dot={STATUS_STYLES[s]?.dot}
						/>
					))}
				</div>
			)}

			{/* Loading skeletons */}
			{offerId && isLoading && (
				<div className="mt-6 space-y-3">
					{["a", "b", "c"].map((k) => (
						<div
							key={k}
							className="flex gap-4 rounded-xl border border-base-300 bg-base-100 p-4"
						>
							<div className="size-14 shrink-0 animate-pulse rounded-xl bg-base-200" />
							<div className="flex-1 space-y-2 py-1">
								<div className="h-4 w-24 animate-pulse rounded-full bg-base-200" />
								<div className="h-5 w-1/2 animate-pulse rounded bg-base-200" />
								<div className="h-4 w-2/3 animate-pulse rounded bg-base-200" />
							</div>
						</div>
					))}
				</div>
			)}

			{/* Empty — offer selected, no requests */}
			{offerId && !isLoading && list.length === 0 && (
				<EmptyState
					icon={<Inbox className="size-8" />}
					title={t("marketplace.incoming.emptyTitle")}
					hint={t("marketplace.incoming.emptyHint")}
				/>
			)}

			{/* Request cards */}
			{offerId && !isLoading && visible.length > 0 && (
				<div className="mt-4 space-y-3">
					{visible.map((req, i) => (
						<RequestCard
							key={req.id}
							req={req}
							index={i}
							headCount={selectedOffer?.headCount ?? 0}
							confirming={confirmingId === req.id}
							typedName={confirmingId === req.id ? typedName : ""}
							accepting={accept.isPending}
							rejecting={reject.isPending}
							onStartAccept={() => {
								setConfirmingId(req.id);
								setTypedName("");
							}}
							onCancelAccept={() => setConfirmingId(null)}
							onTypedNameChange={setTypedName}
							onConfirmAccept={() => {
								if (!selectedOffer) return;
								accept.mutate(
									{
										organizationId: orgId,
										offerId,
										requestId: req.id,
										data: {
											confirmHeadCount: selectedOffer.headCount,
											confirmBuyerOrgName: req.buyerOrganizationName,
										},
									},
									{ onSuccess: () => setConfirmingId(null) },
								);
							}}
							onReject={() =>
								reject.mutate({
									organizationId: orgId,
									offerId,
									requestId: req.id,
									data: {},
								})
							}
						/>
					))}
				</div>
			)}

			{/* Filtered-empty */}
			{offerId && !isLoading && list.length > 0 && visible.length === 0 && (
				<p className="mt-8 text-center text-sm text-base-content/50">
					{t("marketplace.incoming.empty")}
				</p>
			)}
		</div>
	);
}

/* -------------------------------------------------------------------------- */
/*  Shared bits                                                               */
/* -------------------------------------------------------------------------- */

function EmptyState({
	icon,
	title,
	hint,
	cta,
}: {
	icon: React.ReactNode;
	title: string;
	hint: string;
	cta?: React.ReactNode;
}) {
	return (
		<div className="mt-8 flex flex-col items-center rounded-2xl border border-dashed border-base-300 bg-base-100 px-6 py-16 text-center animate-in fade-in">
			<div className="grid size-16 place-items-center rounded-2xl bg-primary/10 text-primary">
				{icon}
			</div>
			<h2 className="mt-5 text-lg font-semibold">{title}</h2>
			<p className="mt-1 max-w-sm text-sm text-base-content/60">{hint}</p>
			{cta}
		</div>
	);
}

function FilterPill({
	active,
	onClick,
	label,
	count,
	dot,
}: {
	active: boolean;
	onClick: () => void;
	label: string;
	count: number;
	dot?: string;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={active}
			className={cn(
				"inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
				active
					? "border-primary bg-primary text-primary-content shadow-sm"
					: "border-base-300 bg-base-100 text-base-content/80 hover:border-primary/40 hover:bg-base-200",
			)}
		>
			{dot && (
				<span
					className={cn(
						"size-1.5 rounded-full",
						active ? "bg-primary-content" : dot,
					)}
				/>
			)}
			{label}
			<span
				className={cn(
					"rounded-full px-1.5 text-xs font-semibold",
					active ? "bg-primary-content/20" : "bg-base-200 text-base-content/60",
				)}
			>
				{count}
			</span>
		</button>
	);
}
